import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  ArrowLeft,
  ArrowRight,
  ShoppingBag,
  LayoutDashboard,
  Building2,
  Code2,
  MessageSquareText,
  ShieldCheck,
  Zap,
  ChevronDown,
  Cloud,
  Clock,
  Calendar,
  UserCheck,
  Search,
  CircleDollarSign,
  Lock,
  User
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const systemTypes = [
  {
    id: 'ecommerce',
    title: 'E-commerce',
    subtitle: 'Tienda en línea o marketplace',
    icon: ShoppingBag
  },
  {
    id: 'erp',
    title: 'Software administrativo / ERP',
    subtitle: 'Operación y gestión del negocio',
    icon: LayoutDashboard
  },
  {
    id: 'web',
    title: 'Web corporativa',
    subtitle: 'Sitio institucional o portal',
    icon: Building2
  },
  {
    id: 'custom',
    title: 'Sistema propio',
    subtitle: 'Aplicación desarrollada a medida',
    icon: Code2
  }
];

const challenges = [
  { id: 'lentitud', label: 'Lentitud' },
  { id: 'obsoleto', label: 'Hosting obsoleto' },
  { id: 'escalar', label: 'Necesito escalar' }
];

const techQuestions = {
  tecnologia: {
    num: '01',
    label: 'Tecnología / Lenguaje actual',
    options: [
      'Sitio web estático / HTML / CSS / JS',
      'PHP / Laravel / WordPress / WooCommerce',
      '.NET / C# / Windows Server',
      'Java / Spring Boot',
      'Python / Node.js / Modern Stack',
      'Sistema Legacy (VB6, FoxPro, Delphi)'
    ]
  },
  entorno: {
    num: '02',
    label: 'Entorno actual del servidor',
    options: [
      'Servidor físico en oficina',
      'Hosting compartido / cPanel',
      'VPS / Servidor virtual (DigitalOcean, Linode, OVH)',
      'Otra nube (AWS, Azure, Huawei)'
    ]
  },
  baseDatos: {
    num: '03',
    label: 'Motor y volumen de Base de Datos',
    options: [
      'Sin base de datos / Archivos planos',
      'MySQL / MariaDB / PostgreSQL (< 50 GB)',
      'SQL Server / Oracle / PostgreSQL (> 50 GB)',
      'Base de datos heredada (DBF, Access, SQL 2000)'
    ]
  },
  arquitectura: {
    num: '04',
    label: 'Arquitectura del software',
    options: [
      'Aplicación web monolítica',
      'Cliente-servidor de escritorio .exe',
      'API / Servicios desacoplados / Contenedores',
      'ERP comercial / Paquete cerrado'
    ]
  },
  concurrencia: {
    num: '05',
    label: 'Concurrencia estimada',
    options: [
      'Operación básica (< 15 usuarios simultáneos)',
      'Operación estándar PYME (< 50 usuarios)',
      'Operación media (50 a 250 usuarios)',
      'Alta demanda / Tráfico público (> 250 usuarios)'
    ]
  }
};

const DiagnosticoGratuito = () => {
  const navigate = useNavigate();

  // Estado del asistente de 3 pasos:
  // 1 = Perfil del sistema | 2 = Evaluación técnica (Ruta A) o Agenda remota (Ruta B) | 3 = Contacto
  const [currentStep, setCurrentStep] = useState(1);

  // Selecciones del Paso 1 (Perfil del Sistema)
  const [selectedSystem, setSelectedSystem] = useState('ecommerce');
  const [selectedChallenge, setSelectedChallenge] = useState('obsoleto');
  // 'no' = Agenda remota con especialista | 'yes' = Cuestionario técnico
  const [hasTechDetail, setHasTechDetail] = useState('yes');

  // Respuestas del Paso 2A (Cuestionario Técnico)
  const [techAnswers, setTechAnswers] = useState({
    tecnologia: '',
    entorno: '',
    baseDatos: '',
    arquitectura: '',
    concurrencia: ''
  });
  const [techError, setTechError] = useState(false);

  // Selecciones del Paso 2B (Agenda Remota de 15 min: Semana actual + Siguiente semana)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('05:30 PM');

  const timeSlots = [
    '10:00 AM',
    '11:30 AM',
    '01:00 PM',
    '04:00 PM',
    '05:30 PM'
  ];

  // Genera los días hábiles (Lun - Vie) de la Semana Actual (offset 0) y la Siguiente Semana (offset 1)
  const getTwoWeeksBusinessDays = () => {
    const today = new Date();
    const todayMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const dayOfWeek = today.getDay(); // 0 = Dom, 1 = Lun ...
    const diffToMonday =
      dayOfWeek === 0 ? 1 : dayOfWeek === 6 ? 2 : 1 - dayOfWeek;
    const baseMonday = new Date(todayMidnight);
    baseMonday.setDate(todayMidnight.getDate() + diffToMonday);

    const shortDayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];
    const fullDayNames = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];

    const buildWeek = (weekIndex) =>
      Array.from({ length: 5 }, (_, idx) => {
        const d = new Date(baseMonday);
        d.setDate(baseMonday.getDate() + weekIndex * 7 + idx);
        const monthName = monthNames[d.getMonth()];
        const capMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        const isPast = d < todayMidnight;
        return {
          id: `w${weekIndex}-d${idx}`,
          weekIndex,
          shortDay: shortDayNames[idx],
          fullDay: fullDayNames[idx],
          dayNumber: d.getDate(),
          monthName,
          isPast,
          headerMonthYear: `${capMonth} ${d.getFullYear()}`,
          fullLabel: `${fullDayNames[idx]} ${d.getDate()} de ${monthName}`
        };
      });

    const currentWeek = buildWeek(0);
    const nextWeek = buildWeek(1);
    const allDays = [...currentWeek, ...nextWeek];
    return { currentWeek, nextWeek, allDays };
  };

  const { currentWeek, nextWeek, allDays } = getTwoWeeksBusinessDays();
  const firstAvailableDay =
    allDays.find((d) => !d.isPast) || nextWeek[0];

  const [selectedDayId, setSelectedDayId] = useState(firstAvailableDay.id);
  const activeDay =
    allDays.find((d) => d.id === selectedDayId && !d.isPast) ||
    firstAvailableDay;

  const handleNextFromStep2B = () => {
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Estado del Paso 3 Final (Datos de Contacto)
  const [contactData, setContactData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [trackingFolio, setTrackingFolio] = useState('FT-GCP-2506-1847');

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData((prev) => ({ ...prev, [name]: value }));
  };

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    contactData.email.trim()
  );

  const selectedSystemObj =
    systemTypes.find((s) => s.id === selectedSystem) || systemTypes[0];

  const handleFinalSubmit = (e) => {
    e.preventDefault();
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const rand = Math.floor(1000 + Math.random() * 9000);
    setTrackingFolio(`FT-GCP-${yy}${mm}-${rand}`);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const step2Label =
    hasTechDetail === 'yes' ? 'Evaluación técnica' : 'Agenda remota';

  const steps = [
    { id: 1, tag: 'PASO 1', label: 'Perfil del sistema' },
    { id: 2, tag: 'PASO 2', label: step2Label },
    { id: 3, tag: 'PASO 3', label: 'Contacto' }
  ];

  const handleNextFromStep1 = () => {
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTechChange = (field, value) => {
    setTechAnswers((prev) => ({ ...prev, [field]: value }));
    if (techError) setTechError(false);
  };

  const isTechComplete = Object.values(techAnswers).every(
    (val) => val.trim() !== ''
  );

  const handleNextFromStep2A = () => {
    if (!isTechComplete) {
      setTechError(true);
      return;
    }
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSelectField = (fieldKey) => {
    const q = techQuestions[fieldKey];
    const value = techAnswers[fieldKey];
    const hasValue = value !== '';

    return (
      <div className="flex items-start gap-3.5">
        <span className="text-xs font-bold text-slate-400 pt-9 w-5 flex-shrink-0">
          {q.num}
        </span>
        <div className="flex-1">
          <label
            htmlFor={`tech-${fieldKey}`}
            className="block text-xs font-bold text-slate-800 mb-2"
          >
            {q.label}
          </label>
          <div className="relative">
            <select
              id={`tech-${fieldKey}`}
              value={value}
              onChange={(e) => handleTechChange(fieldKey, e.target.value)}
              className={`w-full appearance-none rounded-xl px-4 py-3.5 pr-10 text-sm transition-all duration-200 focus:outline-none ${
                hasValue
                  ? 'bg-white border-[1.5px] border-[#00C875] text-slate-900 font-medium shadow-[0_0_0_3px_rgba(0,200,117,0.08)]'
                  : techError
                  ? 'bg-white border border-amber-400 text-slate-500'
                  : 'bg-white border border-slate-200/90 text-slate-400 hover:border-slate-300 focus:border-[#00C875]'
              }`}
            >
              <option value="" disabled>
                Selecciona una opción
              </option>
              {q.options.map((opt) => (
                <option key={opt} value={opt} className="text-slate-900">
                  {opt}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>
    );
  };

  // Pantallas Finales de Confirmación (Paso 8: Ruta A y Ruta B sobre fondo oscuro con retícula)
  if (submitted) {
    return (
      <div className="relative min-h-screen bg-[#0A1124] pt-24 sm:pt-28 flex items-center justify-center px-6 py-12 overflow-hidden">
        {/* Retícula geométrica sutil */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:56px_56px] pointer-events-none"></div>

        {hasTechDetail === 'yes' ? (
          /* Confirmación Ruta A: Evaluación Técnica Recibida + Folio B2B (Tarjeta Blanca) */
          <div className="relative z-10 max-w-xl w-full bg-white rounded-3xl p-8 sm:p-11 shadow-2xl text-center">
            <div className="w-16 h-16 rounded-full bg-[#00C875] flex items-center justify-center mx-auto mb-6 shadow-[0_8px_24px_rgba(0,200,117,0.35)]">
              <Check className="w-7 h-7 text-[#041E13] stroke-[2.8]" />
            </div>

            <p className="text-xs font-bold tracking-widest uppercase text-[#00A862] mb-2">
              EVALUACIÓN ENVIADA
            </p>

            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-3">
              ¡Diagnóstico técnico recibido con éxito!
            </h1>

            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed max-w-md mx-auto mb-7">
              Nuestros Arquitectos Cloud evaluarán la arquitectura de tu sistema. En breve te contactaremos con la viabilidad preliminar y la estimación de tiempos y costos.
            </p>

            {/* Recuadro de Resumen B2B en 2 Columnas */}
            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 sm:p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
              <div className="sm:border-r sm:border-slate-200/80 sm:pr-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  FOLIO DE SEGUIMIENTO B2B
                </p>
                <p className="text-sm font-bold text-slate-900">
                  {trackingFolio}
                </p>
              </div>

              <div className="sm:pl-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                  CORREO CORPORATIVO
                </p>
                <p className="text-sm font-bold text-slate-900 break-all">
                  {contactData.email || 'contacto@empresa.com'}
                </p>
              </div>
            </div>

            <div className="inline-flex items-center justify-center gap-2 text-xs text-slate-500 font-medium mb-7">
              <Clock className="w-4 h-4 text-[#00A862]" strokeWidth={2.2} />
              <span>Tiempo estimado de respuesta: 2 a 3 días hábiles</span>
            </div>

            <div>
              <Button
                type="button"
                onClick={() => navigate('/')}
                className="bg-[#00C875] hover:bg-[#00E082] text-[#041E13] font-bold px-8 py-6 text-sm rounded-xl shadow-[0_8px_24px_rgba(0,200,117,0.35)] hover:shadow-[0_10px_28px_rgba(0,200,117,0.5)] transition-all duration-200 inline-flex items-center gap-2"
              >
                <span>Volver al inicio</span>
                <ArrowRight className="w-4 h-4 stroke-[2.5]" />
              </Button>
            </div>
          </div>
        ) : (
          /* Confirmación Ruta B: Cita Remota Confirmada (Tarjeta Oscura) */
          <div className="relative z-10 max-w-xl w-full bg-[#131E36]/95 backdrop-blur-md border border-white/10 rounded-3xl p-8 sm:p-12 shadow-2xl text-center">
            <div className="w-16 h-16 rounded-full bg-[#00C875] flex items-center justify-center mx-auto mb-6 shadow-[0_8px_24px_rgba(0,200,117,0.35)]">
              <Check className="w-7 h-7 text-[#041E13] stroke-[2.8]" />
            </div>

            <p className="text-xs font-bold tracking-widest uppercase text-[#00C875] mb-3">
              CITA CONFIRMADA
            </p>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
              Tu migración ya tiene un siguiente paso
            </h1>

            <p className="text-xs sm:text-sm text-slate-300/90 leading-relaxed max-w-md mx-auto mb-8">
              Enviamos la invitación y los detalles de la videollamada a tu correo corporativo ({contactData.email || 'tu correo'}).
            </p>

            <Button
              type="button"
              onClick={() => navigate('/')}
              className="bg-[#00C875] hover:bg-[#00E082] text-[#041E13] font-bold px-7 py-6 text-sm rounded-xl shadow-[0_0_28px_rgba(0,200,117,0.4)] hover:shadow-[0_0_34px_rgba(0,200,117,0.6)] transition-all duration-200 inline-flex items-center gap-2"
            >
              <span>Volver al inicio</span>
              <ArrowRight className="w-4 h-4 stroke-[2.5]" />
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F9] pt-24 sm:pt-28 flex flex-col">
      {/* Barra Superior de Progreso (Stepper Horizontal sobre franja blanca) */}
      <div className="w-full bg-white border-b border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.03)]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-5">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const isCompleted = currentStep > step.id;
              const isActive = currentStep === step.id;

              return (
                <React.Fragment key={step.id}>
                  {/* Indicador del Paso */}
                  <button
                    type="button"
                    onClick={() => {
                      if (step.id <= currentStep) setCurrentStep(step.id);
                    }}
                    className={`flex items-center gap-3.5 text-left transition-opacity ${
                      step.id <= currentStep
                        ? 'cursor-pointer'
                        : 'cursor-default'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 flex-shrink-0 ${
                        isCompleted
                          ? 'bg-[#00C875] text-white shadow-[0_0_12px_rgba(0,200,117,0.35)]'
                          : isActive
                          ? 'bg-[#00C875] text-[#041E13] shadow-[0_0_12px_rgba(0,200,117,0.35)]'
                          : 'bg-white border border-slate-200 text-slate-400 font-semibold'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-4 h-4 text-white stroke-[3]" />
                      ) : (
                        step.id
                      )}
                    </div>

                    <div className="hidden sm:block">
                      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold leading-none mb-1">
                        {step.tag}
                      </p>
                      <p
                        className={`text-sm leading-tight transition-colors ${
                          isActive || isCompleted
                            ? 'font-bold text-slate-900'
                            : 'font-medium text-slate-400'
                        }`}
                      >
                        {step.label}
                      </p>
                    </div>
                  </button>

                  {/* Línea conectora entre pasos */}
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-4 sm:mx-8 h-px bg-slate-200 relative overflow-hidden">
                      <div
                        className={`h-full bg-[#00C875] transition-all duration-300 ${
                          currentStep > step.id ? 'w-full' : 'w-0'
                        }`}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contenedor Principal del Paso Activo */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 sm:px-8 py-10 sm:py-12">
        {currentStep === 1 && (
          <div>
            {/* Encabezado de Etapa 1 */}
            <div className="mb-8">
              <p className="text-xs font-bold tracking-wider uppercase text-[#00A862] mb-2">
                PASO 1 DE 3 · PERFIL DEL SISTEMA
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-2">
                Ayúdanos a entender tu operación
              </h1>
              <p className="text-sm sm:text-base text-slate-500">
                No necesitas ser experto técnico. Responde con lo que conoces.
              </p>
            </div>

            {/* Bloque 01: ¿Qué tipo de sistema quieres migrar? */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 mb-5">
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-xs font-bold text-[#00A862]">01</span>
                <h2 className="text-base sm:text-lg font-bold text-slate-900">
                  ¿Qué tipo de sistema quieres migrar?
                </h2>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 pl-7 mb-6">
                Selecciona la opción más cercana.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {systemTypes.map((item) => {
                  const IconComponent = item.icon;
                  const isSelected = selectedSystem === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelectedSystem(item.id)}
                      className={`relative text-left rounded-xl p-5 transition-all duration-200 flex flex-col justify-between min-h-[148px] ${
                        isSelected
                          ? 'border-2 border-[#00C875] bg-[#00C875]/[0.04] shadow-sm'
                          : 'border border-slate-200/90 bg-white hover:border-slate-300 hover:bg-slate-50/50'
                      }`}
                    >
                      {/* Check circular superior derecho cuando está activo */}
                      {isSelected && (
                        <span className="absolute top-3.5 right-3.5 w-5 h-5 rounded-full bg-[#00C875] flex items-center justify-center">
                          <Check className="w-3 h-3 text-white stroke-[3]" />
                        </span>
                      )}

                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-colors ${
                          isSelected
                            ? 'bg-[#00C875]/15 text-[#009958]'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        <IconComponent className="w-5 h-5" strokeWidth={2} />
                      </div>

                      <div>
                        <p className="text-sm font-bold text-slate-900 mb-1 leading-snug">
                          {item.title}
                        </p>
                        <p className="text-xs text-slate-500 leading-relaxed">
                          {item.subtitle}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bloque 02: ¿Cuál es tu principal reto? */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm px-6 py-5 sm:px-8 sm:py-6 mb-5">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-bold text-[#00A862]">02</span>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900">
                    ¿Cuál es tu principal reto?
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full lg:w-auto lg:min-w-[540px]">
                  {challenges.map((challenge) => {
                    const isSelected = selectedChallenge === challenge.id;

                    return (
                      <button
                        key={challenge.id}
                        type="button"
                        onClick={() => setSelectedChallenge(challenge.id)}
                        className={`flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl text-xs sm:text-sm transition-all duration-200 ${
                          isSelected
                            ? 'border-[1.5px] border-[#00C875] bg-[#00C875]/[0.06] text-slate-900 font-bold'
                            : 'border border-slate-200 bg-white text-slate-600 font-medium hover:border-slate-300'
                        }`}
                      >
                        {isSelected ? (
                          <span className="w-4 h-4 rounded-full bg-[#00C875] flex items-center justify-center flex-shrink-0">
                            <Check className="w-2.5 h-2.5 text-white stroke-[3]" />
                          </span>
                        ) : (
                          <span className="w-4 h-4 rounded-full border border-slate-300 flex-shrink-0" />
                        )}
                        <span>{challenge.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Bloque 03: ¿Tienes el detalle técnico de tu infraestructura actual? */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 mb-8">
              <div className="flex items-baseline gap-3 mb-1">
                <span className="text-xs font-bold text-[#00A862]">03</span>
                <h2 className="text-base sm:text-lg font-bold text-slate-900">
                  ¿Tienes el detalle técnico de tu infraestructura actual?
                </h2>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 pl-7 mb-5">
                Elegiremos la mejor ruta para completar tu diagnóstico.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Opción B: No tengo el detalle técnico (Ruta Especialista) */}
                <button
                  type="button"
                  onClick={() => setHasTechDetail('no')}
                  className={`flex items-center justify-between gap-4 p-4 sm:p-5 rounded-xl text-left transition-all duration-200 ${
                    hasTechDetail === 'no'
                      ? 'border-2 border-[#00C875] bg-[#00C875]/[0.04] shadow-sm'
                      : 'border border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        hasTechDetail === 'no'
                          ? 'bg-[#00C875]/15 text-[#009958]'
                          : 'bg-[#00C875]/10 text-[#009958]'
                      }`}
                    >
                      <MessageSquareText className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 mb-0.5">
                        No tengo el detalle técnico
                      </p>
                      <p className="text-xs text-slate-500">
                        Agendar revisión remota con personal de Fieldstech.
                      </p>
                    </div>
                  </div>

                  {hasTechDetail === 'no' ? (
                    <span className="w-5 h-5 rounded-full bg-[#00C875] flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white stroke-[3]" />
                    </span>
                  ) : (
                    <span className="w-5 h-5 rounded-full border border-slate-300 flex-shrink-0" />
                  )}
                </button>

                {/* Opción A: Sí tengo conocimiento técnico (Ruta Cuestionario) */}
                <button
                  type="button"
                  onClick={() => setHasTechDetail('yes')}
                  className={`flex items-center justify-between gap-4 p-4 sm:p-5 rounded-xl text-left transition-all duration-200 ${
                    hasTechDetail === 'yes'
                      ? 'border-2 border-[#00C875] bg-[#00C875]/[0.04] shadow-sm'
                      : 'border border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                        hasTechDetail === 'yes'
                          ? 'bg-[#00C875]/15 text-[#009958]'
                          : 'bg-[#00C875]/10 text-[#009958]'
                      }`}
                    >
                      <Code2 className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900 mb-0.5">
                        Sí tengo conocimiento técnico
                      </p>
                      <p className="text-xs text-slate-500">
                        Responder cuestionario breve de dimensionamiento.
                      </p>
                    </div>
                  </div>

                  {hasTechDetail === 'yes' ? (
                    <span className="w-5 h-5 rounded-full bg-[#00C875] flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white stroke-[3]" />
                    </span>
                  ) : (
                    <span className="w-5 h-5 rounded-full border border-slate-300 flex-shrink-0" />
                  )}
                </button>
              </div>
            </div>

            {/* Pie de Navegación del Paso 1 */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Regresar</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-xs sm:text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                >
                  Cancelar
                </button>
              </div>

              <Button
                type="button"
                onClick={handleNextFromStep1}
                className="w-full sm:w-auto bg-[#00C875] hover:bg-[#00E082] text-[#041E13] font-bold px-7 py-6 text-sm sm:text-base rounded-xl shadow-[0_8px_24px_rgba(0,200,117,0.3)] hover:shadow-[0_10px_28px_rgba(0,200,117,0.45)] transition-all duration-200 flex items-center justify-center gap-2.5"
              >
                <span>
                  {hasTechDetail === 'yes'
                    ? 'Continuar a cuestionario técnico'
                    : 'Continuar con especialista'}
                </span>
                <ArrowRight className="w-4 h-4 stroke-[2.5]" />
              </Button>
            </div>
          </div>
        )}

        {/* Etapa 2A: Cuestionario de Evaluación Técnica */}
        {currentStep === 2 && hasTechDetail === 'yes' && (
          <div>
            {/* Encabezado + Sello de Evaluación Confidencial */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
              <div>
                <p className="text-xs font-bold tracking-wider uppercase text-[#00A862] mb-2">
                  PASO 2 DE 3 · EVALUACIÓN TÉCNICA
                </p>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-2">
                  Cuestionario de Arquitectura e Infraestructura
                </h1>
                <p className="text-sm sm:text-base text-slate-500">
                  Ayúdanos a dimensionar el alcance, esfuerzo y complejidad de tu migración.
                </p>
              </div>

              <div className="bg-[#00C875]/[0.08] border border-[#00C875]/30 rounded-xl px-4 py-3.5 flex items-start gap-3 lg:max-w-xs flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#009958] flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <p className="text-xs font-bold text-slate-900 mb-0.5">
                    Evaluación confidencial
                  </p>
                  <p className="text-[11px] sm:text-xs text-slate-600 leading-snug">
                    Tus respuestas serán revisadas sólo por Arquitectos Cloud.
                  </p>
                </div>
              </div>
            </div>

            {/* Tarjeta Blanca Principal del Cuestionario */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
              {/* Sub-header de la tarjeta */}
              <div className="px-6 py-5 sm:px-8 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#00C875] text-[#041E13] text-xs font-bold flex items-center justify-center flex-shrink-0">
                    05
                  </span>
                  <h2 className="text-sm sm:text-base font-bold text-slate-900">
                    Datos clave de infraestructura
                  </h2>
                </div>
                <span className="text-xs text-slate-400 font-medium">
                  Todos los campos son obligatorios
                </span>
              </div>

              {/* Cuerpo de los 5 Selectores */}
              <div className="p-6 sm:p-8">
                {/* Fila 1: 01 y 02 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 border-b border-slate-100">
                  {renderSelectField('tecnologia')}
                  {renderSelectField('entorno')}
                </div>

                {/* Fila 2: 03 y 04 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-6 border-b border-slate-100">
                  {renderSelectField('baseDatos')}
                  {renderSelectField('arquitectura')}
                </div>

                {/* Fila 3: 05 a ancho completo */}
                <div className="pt-6 mb-6">
                  {renderSelectField('concurrencia')}
                </div>

                {/* Aviso si intenta avanzar sin responder las 5 preguntas */}
                {techError && (
                  <p className="text-xs font-semibold text-amber-600 mb-4">
                    Por favor selecciona una opción en las 5 preguntas para continuar.
                  </p>
                )}

                {/* Banner Informativo Verde Claro */}
                <div className="bg-[#00C875]/[0.07] border border-[#00C875]/20 rounded-xl p-4 flex items-start sm:items-center gap-3.5 mb-8">
                  <Zap className="w-4 h-4 text-[#00A862] flex-shrink-0 mt-0.5 sm:mt-0" strokeWidth={2.3} />
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    <strong className="font-bold text-slate-900 block sm:inline mr-1">
                      Con estas respuestas clasificaremos tu proyecto
                    </strong>
                    como Migración Básica (Lift-and-Shift), Intermedia o Sistema Legacy.
                  </p>
                </div>

                {/* Pie de Navegación dentro de la tarjeta */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
                  <div className="flex items-center gap-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Regresar</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="text-xs sm:text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>

                  <Button
                    type="button"
                    onClick={handleNextFromStep2A}
                    className="w-full sm:w-auto bg-[#00C875] hover:bg-[#00E082] text-[#041E13] font-bold px-7 py-6 text-sm sm:text-base rounded-xl shadow-[0_8px_24px_rgba(0,200,117,0.3)] hover:shadow-[0_10px_28px_rgba(0,200,117,0.45)] transition-all duration-200 flex items-center justify-center gap-2.5"
                  >
                    <span>Continuar a datos de contacto</span>
                    <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Etapa 2B: Agenda de Cita Remota de 15 min */}
        {currentStep === 2 && hasTechDetail === 'no' && (
          <div>
            {/* Encabezado Centrado */}
            <div className="text-center max-w-2xl mx-auto mb-8">
              <p className="text-xs font-bold tracking-wider uppercase text-[#00A862] mb-2">
                PASO 2 DE 3 · AGENDA REMOTA
              </p>
              <h1 className="text-2xl sm:text-3xl lg:text-[34px] font-bold text-slate-900 leading-tight mb-3">
                Agenda revisión remota de 15 min con un Arquitecto Cloud
              </h1>
              <p className="text-sm sm:text-base text-slate-500">
                Elige el momento que mejor te funcione. En el siguiente paso confirmaremos tus datos para enviarte la invitación.
              </p>
            </div>

            {/* Tarjeta Dividida (Split Card) */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-lg overflow-hidden grid grid-cols-1 lg:grid-cols-12 mb-6">
              {/* Panel Izquierdo Oscuro (#0A1124 con retícula) */}
              <div className="lg:col-span-5 bg-[#0A1124] relative p-6 sm:p-8 flex flex-col justify-between text-white">
                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-[#00C875]/15 border border-[#00C875]/25 flex items-center justify-center mb-6">
                    <Cloud className="w-6 h-6 text-[#00C875]" strokeWidth={2} />
                  </div>

                  <p className="text-[11px] font-bold tracking-widest uppercase text-[#00C875] mb-1.5">
                    FIELDSTECH CLOUD
                  </p>
                  <h2 className="text-2xl font-bold text-white mb-2.5">
                    Revisión remota
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-300/90 leading-relaxed pb-6 border-b border-white/10">
                    Una sesión breve y enfocada para conocer tu sistema y resolver tus primeras dudas.
                  </p>

                  <div className="py-6 space-y-3.5 border-b border-white/10">
                    <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                      <Clock className="w-4 h-4 text-[#00C875] flex-shrink-0" strokeWidth={2} />
                      <span>15 minutos</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                      <Calendar className="w-4 h-4 text-[#00C875] flex-shrink-0" strokeWidth={2} />
                      <span>Videollamada por Google Meet</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs sm:text-sm text-slate-200">
                      <UserCheck className="w-4 h-4 text-[#00C875] flex-shrink-0" strokeWidth={2} />
                      <span>Arquitecto Cloud certificado</span>
                    </div>
                  </div>
                </div>

                {/* Zona Horaria con mejora post-evaluación (claridad geográfica) */}
                <div className="relative z-10 pt-6">
                  <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-1">
                    ZONA HORARIA
                  </p>
                  <p className="text-xs sm:text-sm font-bold text-white mb-1">
                    Centro de México (CDMX)
                  </p>
                  <p className="text-[11px] text-slate-400 leading-snug">
                    Atención para toda la República Mexicana y Latinoamérica.
                  </p>
                </div>
              </div>

              {/* Panel Derecho Blanco Interactivo */}
              <div className="lg:col-span-7 p-6 sm:p-8 flex flex-col justify-between bg-white">
                <div>
                  {/* Encabezado de Fecha */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900">
                        Selecciona fecha y hora
                      </h3>
                      <p className="text-xs text-slate-400 font-medium mt-0.5">
                        {activeDay.headerMonthYear}
                      </p>
                    </div>
                  </div>

                  {/* Fila 1: Semana Actual */}
                  <div className="mb-3.5">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                      Semana actual
                    </p>
                    <div className="grid grid-cols-5 gap-2 sm:gap-2.5">
                      {currentWeek.map((day) => {
                        const isSelectedDay = activeDay.id === day.id;
                        return (
                          <button
                            key={day.id}
                            type="button"
                            disabled={day.isPast}
                            onClick={() => setSelectedDayId(day.id)}
                            className={`rounded-xl py-2.5 px-2 flex flex-col items-center justify-center transition-all duration-200 border ${
                              day.isPast
                                ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                                : isSelectedDay
                                ? 'bg-[#0A1124] border-[#0A1124] text-white shadow-md'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <span
                              className={`text-[11px] font-medium mb-0.5 ${
                                day.isPast
                                  ? 'text-slate-300'
                                  : isSelectedDay
                                  ? 'text-slate-300'
                                  : 'text-slate-400'
                              }`}
                            >
                              {day.shortDay}
                            </span>
                            <span className="text-base sm:text-lg font-bold leading-none mb-1.5">
                              {day.dayNumber}
                            </span>
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                day.isPast ? 'bg-slate-200' : 'bg-[#00C875]'
                              }`}
                            ></span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Fila 2: Siguiente Semana */}
                  <div className="mb-6">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                      Siguiente semana
                    </p>
                    <div className="grid grid-cols-5 gap-2 sm:gap-2.5">
                      {nextWeek.map((day) => {
                        const isSelectedDay = activeDay.id === day.id;
                        return (
                          <button
                            key={day.id}
                            type="button"
                            onClick={() => setSelectedDayId(day.id)}
                            className={`rounded-xl py-2.5 px-2 flex flex-col items-center justify-center transition-all duration-200 border ${
                              isSelectedDay
                                ? 'bg-[#0A1124] border-[#0A1124] text-white shadow-md'
                                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <span
                              className={`text-[11px] font-medium mb-0.5 ${
                                isSelectedDay ? 'text-slate-300' : 'text-slate-400'
                              }`}
                            >
                              {day.shortDay}
                            </span>
                            <span className="text-base sm:text-lg font-bold leading-none mb-1.5">
                              {day.dayNumber}
                            </span>
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00C875]"></span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Horarios Disponibles */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-slate-700">
                        Horarios disponibles
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00C875]"></span>
                        Hora CDMX
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {timeSlots.map((slot) => {
                        const isSelectedSlot = selectedTimeSlot === slot;
                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setSelectedTimeSlot(slot)}
                            className={`py-3 px-3 rounded-xl text-xs sm:text-sm transition-all duration-200 flex items-center justify-center gap-1.5 ${
                              isSelectedSlot
                                ? 'border-[1.5px] border-[#00C875] bg-[#00C875]/[0.08] text-slate-900 font-bold'
                                : 'border border-slate-200 bg-white text-slate-700 font-semibold hover:border-slate-300'
                            }`}
                          >
                            {isSelectedSlot && (
                              <Check className="w-3.5 h-3.5 text-[#009958] stroke-[2.8]" />
                            )}
                            <span>{slot}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Caja de Resumen (TU SELECCIÓN) + Botón Continuar */}
                <div>
                  <div className="bg-[#00C875]/[0.06] border border-[#00C875]/25 rounded-xl p-3.5 mb-5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#00C875]/15 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-4 h-4 text-[#009958]" strokeWidth={2.2} />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 leading-none mb-1">
                        TU SELECCIÓN
                      </p>
                      <p className="text-xs sm:text-sm font-bold text-slate-900">
                        {activeDay.fullLabel} · {selectedTimeSlot}
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleNextFromStep2B}
                    className="w-full bg-[#00C875] hover:bg-[#00E082] text-[#041E13] font-bold py-6 text-sm sm:text-base rounded-xl shadow-[0_8px_24px_rgba(0,200,117,0.3)] hover:shadow-[0_10px_28px_rgba(0,200,117,0.45)] transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <span>Continuar para confirmar cita</span>
                    <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Pie Inferior debajo de la tarjeta */}
            <div className="flex items-center justify-between px-1">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Regresar</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-xs sm:text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Etapa 3 (Final): Tus Datos de Contacto */}
        {currentStep === 3 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            {/* Columna Izquierda: Propuesta de Valor, Resumen y Sello Humano */}
            <div className="lg:col-span-6 lg:pr-4 pt-1">
              <div className="flex flex-wrap items-center gap-4 mb-5">
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Regresar al paso anterior</span>
                </button>
                <span className="text-xs font-bold tracking-wider uppercase text-[#00A862]">
                  DIAGNÓSTICO GRATUITO · PASO 3 DE 3
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-slate-900 tracking-tight leading-tight mb-4">
                Cuéntanos sobre tu negocio
              </h1>

              <p className="text-sm sm:text-base text-slate-500 leading-relaxed mb-6">
                Estás a un paso de finalizar. Ingresa tus datos para confirmar{' '}
                {hasTechDetail === 'yes'
                  ? 'tu evaluación técnica y generar tu folio de seguimiento.'
                  : 'tu revisión remota de 15 minutos y enviarte la invitación por correo.'}
              </p>

              {/* Resumen Contextual de lo configurado en los Pasos 1 y 2 */}
              <div className="bg-white border border-[#00C875]/30 rounded-xl p-4 mb-7 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#00A862] mb-1">
                  RESUMEN DE TU SOLICITUD
                </p>
                <p className="text-xs sm:text-sm text-slate-700 font-medium">
                  Sistema: <strong className="text-slate-900">{selectedSystemObj.title}</strong>
                  {' · '}
                  {hasTechDetail === 'no' ? (
                    <>
                      Cita:{' '}
                      <strong className="text-slate-900">
                        {activeDay.fullLabel} a las {selectedTimeSlot}
                      </strong>
                    </>
                  ) : (
                    <>
                      Modalidad:{' '}
                      <strong className="text-slate-900">
                        Cuestionario Técnico Completado (5/5)
                      </strong>
                    </>
                  )}
                </p>
              </div>

              {/* 3 Beneficios de Valor (como en Diagnostico_page.png) */}
              <div className="space-y-5 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00C875]/10 text-[#009958] flex items-center justify-center flex-shrink-0">
                    <Search className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 mb-0.5">
                      Análisis personalizado
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500">
                      Revisamos tus necesidades y objetivos.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00C875]/10 text-[#009958] flex items-center justify-center flex-shrink-0">
                    <CircleDollarSign className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 mb-0.5">
                      Proyección de costos
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500">
                      Visibilidad clara antes de migrar.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00C875]/10 text-[#009958] flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 mb-0.5">
                      Sin compromiso
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500">
                      Una conversación honesta, sin presión.
                    </p>
                  </div>
                </div>
              </div>

              {/* Sello Humano Inferior */}
              <div className="border-t border-slate-200/80 pt-6 flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-[#0A1124] text-white flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-bold text-slate-900">
                    Te responde una persona, no un bot.
                  </p>
                  <p className="text-xs text-slate-500">
                    Nuestro equipo trabaja contigo en español.
                  </p>
                </div>
              </div>
            </div>

            {/* Columna Derecha: Tarjeta Blanca del Formulario */}
            <div className="lg:col-span-6">
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xl p-6 sm:p-9">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-xs font-bold text-[#00A862]">03</span>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    Tus datos de contacto
                  </h2>
                </div>
                <p className="text-xs sm:text-sm text-slate-400 pl-7 mb-6">
                  Empecemos por lo esencial para enviarte tu confirmación.
                </p>

                <form onSubmit={handleFinalSubmit} className="space-y-5">
                  {/* Nombre completo */}
                  <div>
                    <label
                      htmlFor="nombre"
                      className="block text-xs font-bold text-slate-800 mb-2"
                    >
                      Nombre completo <span className="text-[#00A862]">*</span>
                    </label>
                    <Input
                      id="nombre"
                      name="nombre"
                      type="text"
                      required
                      value={contactData.nombre}
                      onChange={handleContactChange}
                      placeholder="Ej. Laura Mendoza"
                      className="h-12 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 border-slate-200 focus-visible:ring-[#00C875]"
                    />
                  </div>

                  {/* Empresa */}
                  <div>
                    <label
                      htmlFor="empresa"
                      className="block text-xs font-bold text-slate-800 mb-2"
                    >
                      Empresa <span className="text-[#00A862]">*</span>
                    </label>
                    <Input
                      id="empresa"
                      name="empresa"
                      type="text"
                      required
                      value={contactData.empresa}
                      onChange={handleContactChange}
                      placeholder="Ej. Acme México"
                      className="h-12 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 border-slate-200 focus-visible:ring-[#00C875]"
                    />
                  </div>

                  {/* Correo corporativo con validación visual en tiempo real */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-xs font-bold text-slate-800 mb-2"
                    >
                      Correo corporativo <span className="text-[#00A862]">*</span>
                    </label>
                    <div className="relative">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={contactData.email}
                        onChange={handleContactChange}
                        placeholder="laura@empresa.com"
                        className={`h-12 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 pr-10 transition-colors ${
                          isEmailValid
                            ? 'border-[#00C875] focus-visible:ring-[#00C875]'
                            : 'border-slate-200 focus-visible:ring-[#00C875]'
                        }`}
                      />
                      {isEmailValid && (
                        <span className="w-5 h-5 rounded-full bg-[#00C875] flex items-center justify-center absolute right-3.5 top-1/2 -translate-y-1/2">
                          <Check className="w-3 h-3 text-white stroke-[3]" />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Teléfono */}
                  <div>
                    <label
                      htmlFor="telefono"
                      className="block text-xs font-bold text-slate-800 mb-2"
                    >
                      Teléfono
                    </label>
                    <Input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      value={contactData.telefono}
                      onChange={handleContactChange}
                      placeholder="+52 55 0000 0000"
                      className="h-12 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 border-slate-200 focus-visible:ring-[#00C875]"
                    />
                  </div>

                  {/* Aviso de Privacidad Humanizado (Mejora Sección 10 del PDF) */}
                  <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-3.5 flex items-start gap-3">
                    <Lock className="w-4 h-4 text-[#009958] flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Tus datos están protegidos por la Ley de Privacidad y bajo estricto acuerdo de confidencialidad comercial. No compartiremos tu información.
                    </p>
                  </div>

                  {/* Botón Final de Conversión */}
                  <Button
                    type="submit"
                    className="w-full bg-[#00C875] hover:bg-[#00E082] text-[#041E13] font-bold py-6 text-sm sm:text-base rounded-xl shadow-[0_8px_24px_rgba(0,200,117,0.3)] hover:shadow-[0_10px_28px_rgba(0,200,117,0.45)] transition-all duration-200 flex items-center justify-center gap-2"
                  >
                    <span>
                      {hasTechDetail === 'yes'
                        ? '¡Enviar diagnóstico técnico!'
                        : '¡Confirmar y agendar cita!'}
                    </span>
                    <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                  </Button>

                  <p className="text-center text-[11px] text-slate-400">
                    Toma menos de un minuto · Sin spam
                  </p>
                </form>

                {submitted && (
                  <div className="mt-4 p-3 rounded-lg bg-[#00C875]/10 border border-[#00C875]/30 text-center text-xs font-semibold text-slate-800">
                    ¡Datos capturados! En el siguiente paso (Paso 8) conectaremos aquí las dos pantallas finales de confirmación (Folio B2B y Cita Confirmada).
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DiagnosticoGratuito;
